import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './AudienceSection.css';
import ArrowRightIcon from '../../../image-assets/icon.arrow.right.svg';

// Ensure GSAP plugin is registered (can also be done globally in App.tsx)
gsap.registerPlugin(ScrollTrigger);

// Interface for a single feature highlight
interface FeatureHighlight {
  title: string;
  description: string;
  docsId?: string; // Add optional docsId
  // icon?: string; // Optional icon for later
}

interface AudienceSectionProps {
  id?: string; // Add optional id prop
  title: string; // Main section title
  description: string; // Main introductory paragraph
  features: FeatureHighlight[]; // Array of 3 feature highlights
  visualContent?: React.ReactNode;
}

const AudienceSection = ({ 
  id, // Add id here
  title, 
  description, 
  features, 
  visualContent, 
}: AudienceSectionProps) => {
  
  const sectionRef = useRef<HTMLElement>(null); // Ref for the main section element
  const containerRef = useRef<HTMLDivElement>(null); // Target for scaling animation

  // Restore useEffect for exit animation
  useEffect(() => {
    if (sectionRef.current && containerRef.current) {

      const randomRotation = gsap.utils.random(-3, 3);

      // --- 1. Entrance Animation (Rotate, Offset, Fade In) --- 
      const entranceAnimation = gsap.fromTo(containerRef.current, 
        { // From state (entering bottom)
          yPercent: 10, 
          rotationZ: randomRotation
        }, 
        { // To state (centered)
          yPercent: 0,
          rotationZ: 0,
          ease: "power1.inOut"
        }
      );

      const entranceTrigger = ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "top bottom",
        end: "center center",
        animation: entranceAnimation,
        scrub: 0.5,
        // markers: {startColor: "blue", endColor: "green"}, 
      });

      // --- 2. Exit Animation (Fade Out Only) --- 
      const fadeOutAnimation = gsap.to(containerRef.current, 
        { // To state (leaving top)
          opacity: 0, // Only fade out
          ease: "power1.inOut"
        }
      );

      const fadeOutTrigger = ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "center center", 
        end: "bottom top",
        animation: fadeOutAnimation,
        scrub: 0.5,
        // markers: {startColor: "green", endColor: "red"}, 
      });

      // Cleanup
      return () => {
        if (entranceTrigger) entranceTrigger.kill();
        if (fadeOutTrigger) fadeOutTrigger.kill(); // Kill the new trigger
      };
    }
  }, []);

  // Visual Placeholder remains the same
  const visualPlaceholder = visualContent;

  return (
    <section 
      id={id} // Pass id to the section element
      ref={sectionRef} 
      className="audience-section"
    >
      {/* containerRef is the target for the scaling animation */}
      <div ref={containerRef} className="audience-section-container">
        <div className="audience-text-content">
          <div>
            <h2 className="audience-title">{title}</h2>
            <p className="audience-description">{description}</p>
          </div>

          <div className="audience-feature-list">
            {features.map((feature) => (
              feature.docsId ? (
                <Link 
                  key={feature.title} 
                  to={`/docs/${feature.docsId}`} 
                  className="audience-feature-item-link"
                >
                  <div className="audience-feature-item-content">
                    <h4 className="audience-feature-title">{feature.title}</h4>
                    <p className="audience-feature-description">{feature.description}</p>
                  </div>
                  <img src={ArrowRightIcon} alt="" className="audience-feature-chevron" />
                </Link>
              ) : (
                <div key={feature.title} className="audience-feature-item-nolink">
                  <div className="audience-feature-item-content">
                    <h4 className="audience-feature-title">{feature.title}</h4>
                    <p className="audience-feature-description">{feature.description}</p>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
        <div className="audience-visual-content">
          {visualPlaceholder}
        </div>
      </div>
    </section>
  );
};

export default AudienceSection; 